import { Box, Button, ButtonGroup, Checkbox, Dialog, Flex, IconButton, Portal, Text, useDisclosure } from '@chakra-ui/react';
import { daysOrder, formatLocationShort, formatWeekday, locations } from '@opanuj/shared/utils';
import { EMPTY_FILTERS, SlotsFilters } from './filters/types';
import { FaUndo } from 'react-icons/fa';
import { FiBook, FiCalendar, FiClock, FiList, FiMapPin, FiPlusSquare, FiUser } from 'react-icons/fi';
import { Location } from '@opanuj/shared/types';
import { SlotFilterSelect } from '../../../components/SlotFilterSelect';
import { SlotForm } from './SlotForm';
import { SlotsDisplayMode } from './SlotsLayout';
import { uniq } from 'lodash';
import { useMemo, useRef } from 'react';

const renderCheckbox = ({
	label,
	checked,
	onChange,
}: {
	label: string;
	checked: boolean;
	onChange: (checked: boolean) => void;
}) => (
	<Checkbox.Root
		size="sm"
		onCheckedChange={(event) => onChange(event.checked === true)}
		checked={checked}
	>
		<Checkbox.HiddenInput />
		<Checkbox.Control>
			<Checkbox.Indicator />
		</Checkbox.Control>
		<Checkbox.Label>{label}</Checkbox.Label>
	</Checkbox.Root>
);

const renderViewModeButtons = ({
	viewMode,
	onViewChange,
}: {
	viewMode: SlotsDisplayMode;
	onViewChange: React.Dispatch<React.SetStateAction<SlotsDisplayMode>>;
}) => (
	<ButtonGroup alignItems="center" variant="outline" size="sm" attached>
		<IconButton
			aria-label="Widok kalendarza"
			variant={viewMode === 'grid' ? 'solid' : 'outline'}
			onClick={() => onViewChange('grid')}
		>
			<FiCalendar />
		</IconButton>
		<IconButton
			aria-label="Widok listy"
			variant={viewMode === 'list' ? 'solid' : 'outline'}
			onClick={() => onViewChange('list')}
		>
			<FiList />
		</IconButton>
	</ButtonGroup>
);

function SlotsToolbar({
	slots,
	filters,
	onFiltersChange,
	viewMode,
	onViewChange,
	visibleSlotCount,
	conflictingSlotsCount,
	selectedSlotId,
}: {
	slots: any[];
	filters: SlotsFilters;
	onFiltersChange: (nextPatch: Partial<SlotsFilters>) => void;
	viewMode: SlotsDisplayMode;
	onViewChange: React.Dispatch<React.SetStateAction<SlotsDisplayMode>>;
	visibleSlotCount: number;
	conflictingSlotsCount: number;
	selectedSlotId: string | null;
}) {
	const teachers = useMemo(
		() => uniq(slots.map((slot) => slot.defaultTeacher).sort()),
		[slots]
	);
	const codes = useMemo(
		() => uniq(slots.map((slot) => slot.code).sort()),
		[slots]
	);
	const durations = useMemo(
		() =>
			uniq(
				slots
					.map((slot) => Number(slot.duration))
					.filter((duration) => Number.isFinite(duration))
					.sort((a, b) => a - b)
			),
		[slots]
	);

	const firstFieldRef = useRef<HTMLElement | null>(null);
	const { open: isAddOpen, onOpen: onAddOpen, onClose: onAddClose } = useDisclosure();

	const clearFilters = () => {
		onFiltersChange(EMPTY_FILTERS);
	};

	const hasAnyFilters =
		Boolean(filters.codeFilter) ||
		Boolean(filters.teacherFilter) ||
		Boolean(filters.dayFilter) ||
		Boolean(filters.durationFilter) ||
		Boolean(filters.locationFilter) ||
		filters.nonEmptyOnly ||
		filters.availableOnly ||
		filters.startedOnly;

	return (
		<Box px={4} py={2}>
			<Flex w="full" alignItems="center" gap={4}>
				<SlotFilterSelect
					icon={FiBook}
					value={filters.codeFilter}
					placeholder="kod zajęć"
					onChange={(value) =>
						onFiltersChange({ codeFilter: Array.isArray(value) ? value[0] ?? '' : value ?? '' })
					}
					options={codes.map((code) => ({ value: code, label: code }))}
				/>
				<SlotFilterSelect
					icon={FiUser}
					value={filters.teacherFilter}
					placeholder="korepetytor"
					onChange={(value) =>
						onFiltersChange({ teacherFilter: Array.isArray(value) ? value[0] ?? '' : value ?? '' })
					}
					options={teachers.map((teacherName) => ({
						value: teacherName,
						label: teacherName,
					}))}
				/>
				<SlotFilterSelect
					icon={FiCalendar}
					value={filters.dayFilter}
					placeholder="dzień"
					onChange={(value) =>
						onFiltersChange({ dayFilter: Array.isArray(value) ? value[0] ?? '' : value ?? '' })
					}
					options={daysOrder.map((day) => ({
						value: day,
						label: formatWeekday(day),
					}))}
				/>
				<SlotFilterSelect
					icon={FiClock}
					value={filters.durationFilter}
					placeholder="długość zajęć"
					onChange={(value) =>
						onFiltersChange({ durationFilter: Array.isArray(value) ? value[0] ?? '' : value ?? '' })
					}
					options={durations.map((duration) => ({
						value: String(duration),
						label: `${duration} min`,
					}))}
				/>
				<SlotFilterSelect
					icon={FiMapPin}
					value={filters.locationFilter}
					placeholder="placówka"
					onChange={(value) =>
						onFiltersChange({ locationFilter: Array.isArray(value) ? value[0] ?? '' : value ?? '' })
					}
					options={locations.map((location) => ({
						value: location,
						label: formatLocationShort(location as Location),
					}))}
				/>
				<IconButton
					onClick={clearFilters}
					size="sm"
					aria-label="Wyczyść filtry"
					variant={hasAnyFilters ? 'solid' : 'outline'}
					flexShrink={0}
				>
					<FaUndo />
				</IconButton>
			</Flex>
			<Flex mt={2} gap={6} alignItems="center" wrap="wrap">

				{renderCheckbox({
					label: 'Tylko rezerwacje',
					checked: filters.nonEmptyOnly,
					onChange: (checked) => onFiltersChange({ nonEmptyOnly: checked }),
				})}
				{renderCheckbox({
					label: 'Tylko dostępne',
					checked: filters.availableOnly,
					onChange: (checked) => onFiltersChange({ availableOnly: checked }),
				})}
				{renderCheckbox({
					label: 'Tylko w grafiku',
					checked: filters.startedOnly,
					onChange: (checked) => onFiltersChange({ startedOnly: checked }),
				})}
				<Flex ms="auto" alignItems="center" gap={3}>
					{selectedSlotId && (
						<Text fontSize="sm" color="gray.600">Wykluczanych: {conflictingSlotsCount}</Text>
					)}
					<Text fontSize="sm" color="gray.600">Wszystkich: {visibleSlotCount}</Text>
					<Button
						size="sm"
						variant="surface"
						colorPalette="green"
						onClick={onAddOpen}
					>
						<FiPlusSquare /> Dodaj termin
					</Button>
				</Flex>
				{renderViewModeButtons({ viewMode, onViewChange })}
			</Flex>
			<Dialog.Root
				open={isAddOpen}
				onOpenChange={(event) => {
					if (event.open) {
						onAddOpen();
					} else {
						onAddClose();
					}
				}}
			>
				<Portal>
					<Dialog.Backdrop />
					<Dialog.Positioner>
						<Dialog.Content maxW="900px" w="95vw">
							<Dialog.Header>
								<Dialog.Title>Dodaj termin</Dialog.Title>
							</Dialog.Header>
							<Dialog.Body>
								<SlotForm
									firstFieldRef={firstFieldRef}
									onCancel={onAddClose}
								/>
							</Dialog.Body>
						</Dialog.Content>
					</Dialog.Positioner>
				</Portal>
			</Dialog.Root>
		</Box>
	);
}

export default SlotsToolbar;